/**
 * Sheet content for role details with tabs for Details, Capabilities, Members
 */
import { Shield } from "lucide-react";
import { useState } from "react";

import { RoleCapabilitiesTab } from "./RoleCapabilitiesTab";
import { RoleDetailsTab } from "./RoleDetailsTab";
import { RoleMembersTab } from "./RoleMembersTab";

import {
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";


interface RoleDetailsSheetProps {
	roleId: string;
	onClose: () => void;
	onUpdate: () => void;
}

/**
 * Sheet content for role details with tabs for Details, Capabilities, Members
 * @param root0 - Component props
 * @param root0.roleId - The ID of the role to display
 * @param root0.onClose - Callback when the sheet is closed
 * @param root0.onUpdate - Callback when the role is updated
 */
export function RoleDetailsSheet({
	roleId,
	onClose: _onClose,
	onUpdate,
}: RoleDetailsSheetProps) {
	const [activeTab, setActiveTab] = useState("details");

	// Fetch role details
	const roleQuery = trpc.roles.get.useQuery({ roleId });

	if (roleQuery.isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
			</div>
		);
	}

	const role = roleQuery.data;
	if (!role) {
		return (
			<div className="py-6">
				<p className="text-muted-foreground">Role not found.</p>
			</div>
		);
	}

	return (
		<>
			<SheetHeader>
				<SheetTitle className="flex items-center gap-2">
					<Shield className="h-5 w-5" />
					{role.name}
				</SheetTitle>
				<SheetDescription>
					{role.description ?? "No description"}
				</SheetDescription>
			</SheetHeader>

			<Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
				<TabsList className="grid w-full grid-cols-3">
					<TabsTrigger value="details">Details</TabsTrigger>
					<TabsTrigger value="capabilities">Capabilities</TabsTrigger>
					<TabsTrigger value="members">Members</TabsTrigger>
				</TabsList>

				<TabsContent value="details" className="mt-4 space-y-4">
					<RoleDetailsTab
						role={role}
						onUpdate={() => {
							void roleQuery.refetch();
							onUpdate();
						}}
					/>
				</TabsContent>

				<TabsContent value="capabilities" className="mt-4 space-y-4">
					<RoleCapabilitiesTab
						role={role}
						onUpdate={() => {
							void roleQuery.refetch();
							onUpdate();
						}}
					/>
				</TabsContent>

				<TabsContent value="members" className="mt-4 space-y-4">
					<RoleMembersTab role={role} />
				</TabsContent>
			</Tabs>
		</>
	);
}
